import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/apiClient'
import { useGlobalState } from '@/stores/globalState'
import type { WeekHistory } from '@/types'

// ---------------------------------------------------------------------------
// Journey response shape from GET /api/journey/learners/:learnerId
// ---------------------------------------------------------------------------

interface JourneyResponse {
  currentWeek: number
  weeklyHistory: WeekHistory[]
}

// ---------------------------------------------------------------------------
// Fetch journey + sync into Zustand
// ---------------------------------------------------------------------------

/**
 * Fetches the learning journey for the active learner and keeps
 * Zustand's `learningJourney` in sync with the server state.
 */
export function useJourney() {
  const activeLearner = useGlobalState((s) => s.activeLearner)
  const setWeeklyHistory = useGlobalState((s) => s.setWeeklyHistory)
  const setCurrentWeek = useGlobalState((s) => s.setCurrentWeek)
  const learnerId = activeLearner?.id ?? null

  const { data, isLoading } = useQuery({
    queryKey: ['journey', learnerId],
    queryFn: async () => {
      const { data } = await apiClient.get<JourneyResponse>(
        `/api/journey/learners/${learnerId}`,
      )
      return data
    },
    enabled: !!learnerId && !!activeLearner?.evaluationComplete,
  })

  // Sync server data into Zustand whenever it changes
  useEffect(() => {
    if (data) {
      setWeeklyHistory(data.weeklyHistory)
      setCurrentWeek(data.currentWeek)
    }
  }, [data, setWeeklyHistory, setCurrentWeek])

  return { journey: data, isLoading }
}

// ---------------------------------------------------------------------------
// Invalidation helper
// ---------------------------------------------------------------------------

export function useInvalidateJourney() {
  const queryClient = useQueryClient()
  const activeLearner = useGlobalState((s) => s.activeLearner)

  return () => {
    if (activeLearner) {
      queryClient.invalidateQueries({ queryKey: ['journey', activeLearner.id] })
    }
  }
}
